import MaskedView from "@react-native-masked-view/masked-view";
import { LinearGradient } from "expo-linear-gradient";
import type { ReactNode } from "react";
import {
  type ImageProps,
  Platform,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
  type StyleProp,
} from "react-native";
import Animated, { type AnimatedProps } from "react-native-reanimated";

import {
  PROFILE_HERO_CONTENT_BOTTOM,
  PROFILE_HERO_CONTENT_PADDING,
  PROFILE_HERO_HEIGHT,
  PROFILE_HERO_MASK_LOCATIONS,
  PROFILE_HERO_TITLE_SIZE,
} from "./recipe";
import type {
  ProfileHeroContentProps,
  ProfileHeroMediaProps,
  ProfileHeroProps,
} from "./types";

export interface ProfileHeroNativeProps extends ProfileHeroProps {
  imageStyle?: AnimatedProps<ImageProps>["style"];
  style?: StyleProp<ViewStyle>;
  titleStyle?: StyleProp<TextStyle>;
}

export interface ProfileHeroMediaNativeProps extends ProfileHeroMediaProps {
  imageStyle?: AnimatedProps<ImageProps>["style"];
  style?: StyleProp<ViewStyle>;
}

export interface ProfileHeroContentNativeProps extends ProfileHeroContentProps {
  style?: StyleProp<ViewStyle>;
  titleStyle?: StyleProp<TextStyle>;
}

const PROFILE_HERO_MASK_COLORS = [
  "#000000",
  "#000000",
  "rgba(0, 0, 0, 0.95)",
  "rgba(0, 0, 0, 0.72)",
  "rgba(0, 0, 0, 0.34)",
  "rgba(0, 0, 0, 0.08)",
  "transparent",
] as const;

/**
 * `@react-native-masked-view/masked-view` has no web implementation: it
 * renders the mask element and drops the children, so the hero showed the
 * black-to-transparent mask instead of the image on the web app. On web the
 * same alpha stops are applied as a CSS mask on a plain DOM element instead.
 */
function WebProfileHeroMask({ children }: { children: ReactNode }) {
  const stops = PROFILE_HERO_MASK_COLORS.map(
    (color, index) => `${color} ${PROFILE_HERO_MASK_LOCATIONS[index] * 100}%`,
  ).join(", ");
  const maskImage = `linear-gradient(to bottom, ${stops})`;

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        WebkitMaskImage: maskImage,
        maskImage,
        WebkitMaskSize: "100% 100%",
        maskSize: "100% 100%",
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
      }}
    >
      {children}
    </div>
  );
}

function ProfileHeroMedia({
  imageStyle,
  imageUrl,
  style,
}: ProfileHeroMediaNativeProps) {
  const media = (
    <View style={styles.maskedMedia}>
      {imageUrl ? (
        <Animated.Image
          resizeMode="cover"
          source={{ uri: imageUrl }}
          style={[styles.image, imageStyle]}
        />
      ) : null}
      <LinearGradient
        colors={["rgba(0, 0, 0, 0.46)", "rgba(0, 0, 0, 0.18)", "transparent"]}
        end={{ x: 0, y: 1 }}
        locations={[0, 0.2, 0.44]}
        pointerEvents="none"
        start={{ x: 0, y: 0 }}
        style={StyleSheet.absoluteFill}
      />
    </View>
  );

  return (
    <View pointerEvents="none" style={[styles.media, style]}>
      {Platform.OS === "web" ? (
        <WebProfileHeroMask>{media}</WebProfileHeroMask>
      ) : (
        <MaskedView
          style={StyleSheet.absoluteFill}
          maskElement={
            <LinearGradient
              colors={[...PROFILE_HERO_MASK_COLORS]}
              end={{ x: 0, y: 1 }}
              locations={[...PROFILE_HERO_MASK_LOCATIONS]}
              start={{ x: 0, y: 0 }}
              style={styles.mask}
            />
          }
        >
          {media}
        </MaskedView>
      )}
    </View>
  );
}

function ProfileHeroContent({
  children,
  style,
  title,
  titleStyle,
}: ProfileHeroContentNativeProps) {
  return (
    <View style={[styles.content, style]}>
      <Text numberOfLines={1} style={[styles.title, titleStyle]}>
        {title}
      </Text>
      {children ? <View style={styles.belowTitle}>{children}</View> : null}
    </View>
  );
}

function ProfileHeroFrame({
  children,
  imageStyle,
  imageUrl,
  style,
  title,
  titleStyle,
}: ProfileHeroNativeProps) {
  return (
    <View style={[styles.frame, style]}>
      <ProfileHeroMedia imageStyle={imageStyle} imageUrl={imageUrl} />
      <ProfileHeroContent title={title} titleStyle={titleStyle}>
        {children}
      </ProfileHeroContent>
    </View>
  );
}

export const ProfileHero = Object.assign(ProfileHeroFrame, {
  Content: ProfileHeroContent,
  Media: ProfileHeroMedia,
});

const styles = StyleSheet.create({
  frame: {
    position: "relative",
    width: "100%",
    height: PROFILE_HERO_HEIGHT,
  },
  media: {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
  },
  maskedMedia: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: "#292824",
  },
  mask: {
    width: "100%",
    height: "100%",
  },
  image: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: "100%",
    height: "100%",
  },
  content: {
    position: "absolute",
    zIndex: 1,
    right: PROFILE_HERO_CONTENT_PADDING,
    bottom: PROFILE_HERO_CONTENT_BOTTOM,
    left: PROFILE_HERO_CONTENT_PADDING,
    minWidth: 0,
  },
  title: {
    color: "#FFFFFF",
    fontSize: PROFILE_HERO_TITLE_SIZE,
    fontWeight: "800",
    lineHeight: 36,
    letterSpacing: -0.8,
    textShadowColor: "rgba(0, 0, 0, 0.62)",
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 8,
  },
  belowTitle: {
    marginTop: 14,
  },
});
